import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { from, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

/**
 * Adds the Keycloak bearer token to outgoing requests, replacing the bearer
 * interceptor that keycloak-angular's KeycloakService used to provide
 * (enableBearerInterceptor, removed in v19+). Registered as a DI HTTP_INTERCEPTORS
 * entry — the same mechanism the old interceptor used.
 *
 * Like the removed KeycloakBearerInterceptor, it REFRESHES the access token before
 * attaching it: keycloak.updateToken(minValidity) renews the token when it is about to
 * expire (no-op/no network while it is still valid for >minValidity seconds). Without
 * this the short-lived cached token is attached until it expires, so after a few minutes
 * the web services start returning 401 until the page is reloaded.
 *
 * Mirrors ewsportal/src/app/auth/keycloak-bearer.interceptor.ts; excluded URLs match the
 * former bearerExcludedUrls passed to KeycloakService.init().
 */
const BEARER_EXCLUDED_URLS: readonly string[] = ['/dashboard', '/resident-portal'];

/** Refresh the token if it expires within this many seconds (covers in-flight request time). */
const TOKEN_MIN_VALIDITY_SECONDS = 30;

@Injectable()
export class KeycloakBearerInterceptor implements HttpInterceptor {
	private readonly keycloak = inject(Keycloak);

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const isExcluded = BEARER_EXCLUDED_URLS.some((url) => req.url.includes(url));
		if (isExcluded || !this.keycloak?.authenticated) {
			return next.handle(req);
		}

		// Proactively refresh the (short-lived) access token before each request, then attach the
		// fresh one. updateToken resolves without a network call while the token is still valid.
		return from(this.keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS)).pipe(
			// Refresh failed (e.g. the SSO session / refresh token has truly expired) — fall through
			// with whatever token we have; the resulting 401 reflects a genuinely ended session.
			catchError(() => of(false)),
			switchMap(() => {
				const token = this.keycloak.token;
				const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
				return next.handle(authReq);
			})
		);
	}
}
